import { cvData } from '../data/cvData';

export function generateCvHtml(defaultLang = 'en') {
  const serializedEn = JSON.stringify(cvData.en).replace(/</g, '\\u003c');
  const serializedAr = JSON.stringify(cvData.ar).replace(/</g, '\\u003c');

  return `<!DOCTYPE html>
<html lang="${defaultLang}" dir="${defaultLang === 'ar' ? 'rtl' : 'ltr'}">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Samir Djelal - Executive CV (Group CTO & Solutions Officer)</title>
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700;800;900&family=Inter:wght@300;400;500;600;700;800&family=JetBrains+Mono:wght@400;500&display=swap" rel="stylesheet">
  <style>
    :root {
      --font-en: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
      --font-ar: 'Cairo', -apple-system, BlinkMacSystemFont, "Segoe UI", Tahoma, sans-serif;
      --font-mono: 'JetBrains Mono', monospace;
      
      --color-primary: #b45309;
      --color-primary-light: #fef3c7;
      --color-primary-dark: #92400e;
      --color-accent: #0f172a;
      
      --text-heading: #0f172a;
      --text-body: #334155;
      --text-muted: #64748b;
      --text-light: #94a3b8;
      
      --bg-page: #f8fafc;
      --bg-card: #ffffff;
      --bg-tag: #f1f5f9;
      --border: #e2e8f0;
      --border-subtle: #cbd5e1;
    }

    * {
      box-sizing: border-box;
      margin: 0;
      padding: 0;
    }

    body {
      background-color: var(--bg-page);
      color: var(--text-body);
      font-family: var(--font-en);
      font-size: 13.5px;
      line-height: 1.55;
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
      padding-top: 60px;
      padding-bottom: 50px;
    }

    html[dir="rtl"] body {
      font-family: var(--font-ar);
      font-size: 14px;
      line-height: 1.65;
    }

    /* Floating Screen Action Bar */
    .action-bar {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      height: 56px;
      background: rgba(15, 23, 42, 0.95);
      backdrop-filter: blur(12px);
      -webkit-backdrop-filter: blur(12px);
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 0 24px;
      z-index: 9999;
      color: #fff;
    }

    .action-bar-left {
      display: flex;
      align-items: center;
      gap: 12px;
      font-size: 13px;
      font-weight: 600;
    }

    .badge-live {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 4px 10px;
      background: rgba(16, 185, 129, 0.15);
      border: 1px solid rgba(16, 185, 129, 0.3);
      color: #34d399;
      border-radius: 9999px;
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0.05em;
      text-transform: uppercase;
    }

    .badge-live span {
      width: 6px;
      height: 6px;
      background-color: #34d399;
      border-radius: 50%;
      animation: pulse 2s infinite;
    }

    @keyframes pulse {
      0%, 100% { opacity: 1; transform: scale(1); }
      50% { opacity: 0.4; transform: scale(0.85); }
    }

    .action-bar-right {
      display: flex;
      align-items: center;
      gap: 10px;
    }

    .btn {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 8px 16px;
      border-radius: 8px;
      font-size: 13px;
      font-weight: 600;
      cursor: pointer;
      text-decoration: none;
      transition: all 0.15s ease;
      border: 1px solid transparent;
    }

    .btn-primary {
      background: #f59e0b;
      color: #000;
      border-color: #d97706;
      box-shadow: 0 2px 6px rgba(245, 158, 11, 0.3);
    }

    .btn-primary:hover {
      background: #fbbf24;
      transform: translateY(-1px);
    }

    .btn-secondary {
      background: rgba(255, 255, 255, 0.08);
      color: #f8fafc;
      border-color: rgba(255, 255, 255, 0.15);
    }

    .btn-secondary:hover {
      background: rgba(255, 255, 255, 0.15);
      color: #ffffff;
    }

    /* Main CV Sheet Container */
    .cv-wrapper {
      max-width: 860px;
      margin: 20px auto 40px auto;
      background: var(--bg-card);
      border-radius: 12px;
      box-shadow: 0 10px 30px -5px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.04);
      padding: 44px 50px;
      border: 1px solid var(--border);
    }

    /* CV Header */
    .cv-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 30px;
      padding-bottom: 24px;
      border-bottom: 2px solid var(--border);
      margin-bottom: 24px;
    }

    .header-primary h1 {
      font-size: 32px;
      font-weight: 800;
      color: var(--text-heading);
      letter-spacing: -0.03em;
      line-height: 1.15;
      margin-bottom: 4px;
    }

    .header-primary .title {
      font-size: 17px;
      font-weight: 700;
      color: var(--color-primary-dark);
      margin-bottom: 4px;
    }

    .header-primary .scope {
      font-size: 12.5px;
      font-weight: 600;
      color: var(--text-muted);
      margin-bottom: 6px;
    }

    .header-primary .tagline {
      font-size: 12px;
      color: var(--text-body);
      font-weight: 500;
      max-width: 520px;
      line-height: 1.45;
    }

    .contact-block {
      display: flex;
      flex-direction: column;
      gap: 6px;
      font-size: 12px;
      color: var(--text-muted);
      min-width: 220px;
      flex-shrink: 0;
    }

    html[dir="rtl"] .contact-block {
      text-align: right;
    }

    .contact-item {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .contact-item svg {
      width: 14px;
      height: 14px;
      color: var(--text-muted);
      flex-shrink: 0;
    }

    .contact-item a {
      color: var(--text-heading);
      text-decoration: none;
      font-weight: 500;
      transition: color 0.15s ease;
    }

    .contact-item a:hover {
      color: var(--color-primary);
      text-decoration: underline;
    }

    /* Executive Proof Badges / Highlights */
    .highlights-grid {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 10px;
      margin-bottom: 26px;
    }

    .highlight-card {
      background: #f8fafc;
      border: 1px solid var(--border);
      border-radius: 8px;
      padding: 10px 12px;
      position: relative;
    }

    .highlight-card .badge {
      display: inline-block;
      font-size: 10px;
      font-weight: 800;
      color: var(--color-primary-dark);
      background: var(--color-primary-light);
      padding: 2px 6px;
      border-radius: 4px;
      margin-bottom: 4px;
      letter-spacing: 0.02em;
    }

    .highlight-card .hl-title {
      font-size: 11.5px;
      font-weight: 700;
      color: var(--text-heading);
      margin-bottom: 2px;
    }

    .highlight-card .hl-detail {
      font-size: 10.5px;
      color: var(--text-muted);
      line-height: 1.35;
    }

    /* Section Headings */
    .cv-section {
      margin-bottom: 26px;
      page-break-inside: auto;
      break-inside: auto;
    }

    .cv-section h2 {
      font-size: 13px;
      font-weight: 800;
      text-transform: uppercase;
      letter-spacing: 0.08em;
      color: var(--text-heading);
      margin-bottom: 12px;
      display: flex;
      align-items: center;
      gap: 10px;
      border-bottom: 1.5px solid var(--border);
      padding-bottom: 6px;
    }

    .cv-section h2 .sec-bar {
      width: 4px;
      height: 14px;
      background-color: #f59e0b;
      border-radius: 2px;
    }

    /* Executive Summary Text */
    .summary-text {
      font-size: 13px;
      line-height: 1.6;
      color: var(--text-body);
      text-align: justify;
      background: #fafafa;
      border-left: 3px solid #f59e0b;
      padding: 12px 16px;
      border-radius: 0 8px 8px 0;
    }

    html[dir="rtl"] .summary-text {
      border-left: none;
      border-right: 3px solid #f59e0b;
      border-radius: 8px 0 0 8px;
    }

    /* Skills Grid */
    .skills-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 14px 18px;
    }

    .skill-cat {
      background: #f8fafc;
      border: 1px solid var(--border);
      border-radius: 8px;
      padding: 12px 14px;
      break-inside: avoid;
      page-break-inside: avoid;
    }

    .skill-cat-title {
      font-size: 12px;
      font-weight: 700;
      color: var(--text-heading);
      margin-bottom: 8px;
      display: flex;
      align-items: center;
      gap: 6px;
    }

    .skill-pills {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
    }

    .pill {
      font-size: 11px;
      background: #ffffff;
      border: 1px solid var(--border-subtle);
      color: var(--text-heading);
      padding: 2.5px 8px;
      border-radius: 4px;
      font-weight: 500;
    }

    /* Timeline & Experience Items */
    .timeline-item {
      margin-bottom: 20px;
      padding-bottom: 18px;
      border-bottom: 1px dashed var(--border);
      break-inside: avoid;
      page-break-inside: avoid;
    }

    .timeline-item:last-child {
      border-bottom: none;
      margin-bottom: 0;
      padding-bottom: 0;
    }

    .item-top {
      display: flex;
      justify-content: space-between;
      align-items: baseline;
      margin-bottom: 3px;
    }

    .item-title {
      font-size: 15px;
      font-weight: 700;
      color: var(--text-heading);
    }

    .item-period {
      font-size: 12px;
      font-weight: 600;
      color: var(--color-primary-dark);
      white-space: nowrap;
    }

    .item-sub {
      font-size: 12.5px;
      font-weight: 600;
      color: var(--text-muted);
      margin-bottom: 6px;
    }

    .item-desc {
      font-size: 12.5px;
      line-height: 1.55;
      color: var(--text-body);
      margin-bottom: 8px;
      text-align: justify;
    }

    .item-tags {
      display: flex;
      flex-wrap: wrap;
      gap: 5px;
    }

    .tag-sm {
      font-size: 10.5px;
      background: var(--bg-tag);
      color: var(--text-muted);
      border: 1px solid var(--border);
      padding: 1.5px 6px;
      border-radius: 4px;
      font-weight: 500;
    }

    /* Print Specific Tweaks */
    @media print {
      body {
        padding-top: 0;
        padding-bottom: 0;
        background: #fff;
        color: #000;
      }

      .action-bar {
        display: none !important;
      }

      .cv-wrapper {
        margin: 0;
        padding: 0;
        border: none;
        box-shadow: none;
        max-width: 100%;
        border-radius: 0;
      }

      .highlight-card {
        background: #fafafa !important;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }

      .skill-cat {
        background: #fafafa !important;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }

      .badge {
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }

      .pill {
        background: #fff !important;
        border-color: #ccc !important;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }

      .summary-text {
        background: #fafafa !important;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }

      .cv-section h2 .sec-bar {
        background-color: #b45309 !important;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }

      a {
        text-decoration: none;
        color: #000;
      }

      @page {
        size: A4 portrait;
        margin: 12mm 14mm 12mm 14mm;
      }
    }

    @media (max-width: 768px) {
      .cv-wrapper {
        margin: 10px;
        padding: 24px 20px;
      }

      .cv-header {
        flex-direction: column;
        gap: 16px;
      }

      .highlights-grid {
        grid-template-columns: 1fr 1fr;
      }

      .skills-grid {
        grid-template-columns: 1fr;
      }

      .item-top {
        flex-direction: column;
        gap: 2px;
      }
    }
  </style>
</head>
<body>
  <!-- Floating Screen Action Bar -->
  <header class="action-bar" id="actionBar">
    <div class="action-bar-left">
      <span class="badge-live"><span></span> Executive CV</span>
      <span style="opacity: 0.7; font-weight: 400; font-size: 12px;">Verified Profile • 2026</span>
    </div>
    <div class="action-bar-right">
      <button class="btn btn-secondary" id="langSwitchBtn" onclick="toggleLanguage()">
        ${defaultLang === 'ar' ? 'English' : 'العربية (Arabic)'}
      </button>
      <button class="btn btn-primary" onclick="window.print()">
        <svg width="15" height="15" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
        </svg>
        <span id="printLabelText">${defaultLang === 'ar' ? 'طباعة / حفظ كملف PDF' : 'Print / Save as PDF'}</span>
      </button>
      <a class="btn btn-secondary" href="https://samirdjelal.com" target="_blank" rel="noopener noreferrer">
        samirdjelal.com
      </a>
    </div>
  </header>

  <!-- CV Sheet Content -->
  <main class="cv-wrapper" id="cvContentArea">
    <!-- Populated by JavaScript renderContent -->
  </main>

  <script>
    const dataStore = {
      en: ${serializedEn},
      ar: ${serializedAr}
    };

    let currentLang = "${defaultLang}";

    function renderCV(lang) {
      const data = dataStore[lang] || dataStore.en;
      const p = data.profile;
      const s = data.sections;

      document.documentElement.lang = data.lang;
      document.documentElement.dir = data.dir;

      // Update button text
      const switchBtn = document.getElementById('langSwitchBtn');
      if (switchBtn) {
        switchBtn.textContent = lang === 'ar' ? 'English' : 'العربية (Arabic)';
      }
      const printLabel = document.getElementById('printLabelText');
      if (printLabel) {
        printLabel.textContent = s.printButton;
      }

      const container = document.getElementById('cvContentArea');
      if (!container) return;

      container.innerHTML = \`
        <!-- Header -->
        <header class="cv-header">
          <div class="header-primary">
            <h1>\${p.name}</h1>
            <div class="title">\${p.title}</div>
            <div class="scope">\${p.scope}</div>
            <div class="tagline">\${p.tagline}</div>
          </div>
          <div class="contact-block">
            <div class="contact-item">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>
              <a href="mailto:\${p.email}">\${p.email}</a>
            </div>
            <div class="contact-item">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
              <span>\${p.location}</span>
            </div>
            <div class="contact-item">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"/></svg>
              <a href="\${p.website}" target="_blank">\${p.websiteLabel}</a>
            </div>
            <div class="contact-item">
              <svg fill="currentColor" viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/></svg>
              <a href="\${p.linkedin}" target="_blank">\${p.linkedinLabel}</a>
            </div>
            <div class="contact-item">
              <svg fill="currentColor" viewBox="0 0 24 24"><path fill-rule="evenodd" clip-rule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.53 1.032 1.53 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z"/></svg>
              <a href="\${p.github}" target="_blank">\${p.githubLabel}</a>
            </div>
          </div>
        </header>

        <!-- Highlights Ribbon -->
        <section class="highlights-grid">
          \${data.achievements.map(a => \`
            <div class="highlight-card">
              <span class="badge">\${a.badge}</span>
              <div class="hl-title">\${a.title}</div>
              <div class="hl-detail">\${a.detail}</div>
            </div>
          \`).join('')}
        </section>

        <!-- Executive Summary -->
        <section class="cv-section">
          <h2><span class="sec-bar"></span>\${s.summary}</h2>
          <div class="summary-text">\${p.summary}</div>
        </section>

        <!-- Core Stack & Systems -->
        <section class="cv-section">
          <h2><span class="sec-bar"></span>\${s.skills}</h2>
          <div class="skills-grid">
            \${data.skillCategories.map(cat => \`
              <div class="skill-cat">
                <div class="skill-cat-title">\${cat.category}</div>
                <div class="skill-pills">
                  \${cat.skills.map(sk => \`<span class="pill">\${sk}</span>\`).join('')}
                </div>
              </div>
            \`).join('')}
          </div>
        </section>

        <!-- Professional Experience -->
        <section class="cv-section">
          <h2><span class="sec-bar"></span>\${s.experience}</h2>
          \${data.experiences.map(exp => \`
            <article class="timeline-item">
              <div class="item-top">
                <h3 class="item-title">\${exp.title}</h3>
                <span class="item-period">\${exp.period}</span>
              </div>
              <div class="item-sub">\${exp.company}\${exp.location ? \` • \${exp.location}\` : ''}</div>
              \${exp.description ? \`<p class="item-desc">\${exp.description}</p>\` : ''}
              \${exp.skills && exp.skills.length ? \`
                <div class="item-tags">
                  \${exp.skills.map(sk => \`<span class="tag-sm">\${sk}</span>\`).join('')}
                </div>
              \` : ''}
            </article>
          \`).join('')}
        </section>

        <!-- Education -->
        <section class="cv-section">
          <h2><span class="sec-bar"></span>\${s.education}</h2>
          \${data.education.map(edu => \`
            <article class="timeline-item">
              <div class="item-top">
                <h3 class="item-title">\${edu.degree}</h3>
                <span class="item-period">\${edu.period}</span>
              </div>
              <div class="item-sub">\${edu.institution}</div>
              \${edu.description ? \`<p class="item-desc">\${edu.description}</p>\` : ''}
              \${edu.skills && edu.skills.length ? \`
                <div class="item-tags">
                  \${edu.skills.map(sk => \`<span class="tag-sm">\${sk}</span>\`).join('')}
                </div>
              \` : ''}
            </article>
          \`).join('')}
        </section>
      \`;
    }

    function toggleLanguage() {
      currentLang = currentLang === 'en' ? 'ar' : 'en';
      renderCV(currentLang);
    }

    // Initial render
    renderCV(currentLang);
  </script>
</body>
</html>`;
}

export default generateCvHtml;
